import { useMemo } from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import { useI18n } from '@xdocs/ui';
import type { TranslateVars } from '@xdocs/i18n';
import type { JobsCatalogKey } from './jobs.i18n.js';
import { JOBS_CATALOG } from './jobs.i18n.js';

export type JobsMessageId = `jobs:${JobsCatalogKey}`;

const PREFIX_LENGTH = 'jobs:'.length;

/** `t()` for the stop-gap `jobs:` scope (see `jobs.i18n.ts`). Shares
 * `useI18n()`'s current locale so it stays in lockstep with the rest of the
 * app — never a second locale source. */
export function useJobsT(): { t: (id: JobsMessageId, vars?: TranslateVars) => string } {
  const { locale } = useI18n();

  const t = useMemo(() => {
    return (id: JobsMessageId, vars?: TranslateVars): string => {
      const key = id.slice(PREFIX_LENGTH) as JobsCatalogKey;
      const raw = JOBS_CATALOG[locale][key] ?? JOBS_CATALOG.en[key];
      if (!vars) return raw;
      const formatted = new IntlMessageFormat(raw, locale).format(vars);
      if (formatted == null) return '';
      if (typeof formatted === 'string') return formatted;
      return Array.isArray(formatted) ? formatted.map(String).join('') : String(formatted);
    };
  }, [locale]);

  return { t };
}
